import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { api, API } from "../../lib/api";
import { useAuth, canEdit } from "../../lib/auth";
import { toast } from "sonner";
import { Plus, Upload, Download, Trash2, FolderOpen, FileText, History, X, Link as LinkIcon, ExternalLink } from "lucide-react";
import { Modal, TextField, TextArea, SelectField } from "./SponsorsPage";

const LINK_ICONS = [
  { value: "link", label: "Generic Link" },
  { value: "google_drive", label: "Google Drive" },
  { value: "google_docs", label: "Google Docs" },
  { value: "google_forms", label: "Google Forms" },
  { value: "eventbrite", label: "Eventbrite" },
  { value: "zeffy", label: "Zeffy" },
  { value: "youtube", label: "YouTube" },
  { value: "vimeo", label: "Vimeo" },
  { value: "canva", label: "Canva" },
  { value: "dropbox", label: "Dropbox" },
  { value: "sharepoint", label: "SharePoint" },
  { value: "other", label: "Other" },
];

const CATEGORIES = [
  { value: "sponsorship", label: "Sponsorship" },
  { value: "event_ops", label: "Event Operations" },
  { value: "volunteer", label: "Volunteer" },
  { value: "vendor", label: "Vendor" },
  { value: "media", label: "Media" },
  { value: "awards", label: "Awards & Scholarship" },
];
const CAT_VALUES = CATEGORIES.map((c) => c.value);
const STATUSES = ["draft", "review", "published", "archived"];

const STATUS_COLOR = {
  draft: "text-white/60", review: "text-yellow-300",
  published: "text-aawta-pink", archived: "text-white/30",
};

export default function DocumentsPage() {
  const { user } = useAuth();
  const editable = canEdit(user);
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [creating, setCreating] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [uploadingVersion, setUploadingVersion] = useState(null);
  const [creatingLink, setCreatingLink] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/admin/documents", { withCredentials: true });
    setDocs(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = filter === "all" ? docs : docs.filter((d) => d.category === filter);

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!creating.file) { toast.error("Please choose a file"); return; }
    setSaving(true);
    const fd = new FormData();
    ["title", "category", "description", "status", "assigned_owner_email", "notes"].forEach((k) => {
      if (creating[k]) fd.append(k, creating[k]);
    });
    fd.append("file", creating.file);
    try {
      await axios.post(`${API}/admin/documents`, fd, { withCredentials: true });
      toast.success("Document uploaded");
      setCreating(null);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setSaving(false); }
  };

  const submitVersion = async (e) => {
    e.preventDefault();
    if (!uploadingVersion.file) { toast.error("Choose a file"); return; }
    setSaving(true);
    const fd = new FormData();
    if (uploadingVersion.notes) fd.append("notes", uploadingVersion.notes);
    fd.append("file", uploadingVersion.file);
    try {
      await axios.post(`${API}/admin/documents/${uploadingVersion.doc_id}/versions`, fd, { withCredentials: true });
      toast.success("New version uploaded");
      setUploadingVersion(null);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setSaving(false); }
  };

  const updateMeta = async (doc, patch) => {
    await api.patch(`/admin/documents/${doc.id}`, {
      title: doc.title, category: doc.category, description: doc.description,
      status: doc.status, assigned_owner_email: doc.assigned_owner_email, ...patch,
    }, { withCredentials: true });
    toast.success("Updated");
    await load();
  };

  const download = async (doc, version) => {
    try {
      const r = await axios.get(`${API}/admin/documents/${doc.id}/download${version ? `?version=${version}` : ""}`, {
        withCredentials: true, responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const v = version || doc.current_version;
      const original = doc.versions.find((x) => x.version === v)?.original_filename || "download";
      a.download = original;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const remove = async (doc) => {
    if (!window.confirm("Archive this document? It will be hidden from active lists.")) return;
    await api.delete(`/admin/documents/${doc.id}`, { withCredentials: true });
    toast.success("Archived");
    await load();
  };

  const catLabel = (v) => CATEGORIES.find((c) => c.value === v)?.label || v;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-overline text-aawta-pink mb-2">DOCUMENT CENTER</p>
          <h1 className="font-display text-4xl text-white">Documents</h1>
          <p className="text-white/50 mt-2 text-sm">Sponsorship decks, run-of-show, vendor contracts, media kits, and more — all in one place with version history.</p>
        </div>
        {editable && (
          <div className="flex items-center gap-2">
            <button data-testid="add-link-btn" onClick={() => setCreatingLink({ title: "", category: "sponsorship", external_url: "", icon: "link", description: "", status: "published" })} className="btn-ghost py-2.5 px-4">
              <LinkIcon size={14} /> ADD LINK
            </button>
            <button data-testid="add-doc-btn" onClick={() => setCreating({ title: "", category: "sponsorship", description: "", status: "draft", assigned_owner_email: "", notes: "", file: null })} className="btn-pink">
              <Plus size={16} /> UPLOAD DOCUMENT
            </button>
          </div>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        <button data-testid="cat-all" onClick={() => setFilter("all")} className={`text-overline px-3 py-1.5 border ${filter === "all" ? "border-aawta-pink text-aawta-pink" : "border-white/15 text-white/60"}`}>ALL</button>
        {CATEGORIES.map((c) => (
          <button key={c.value} data-testid={`cat-${c.value}`} onClick={() => setFilter(c.value)} className={`text-overline px-3 py-1.5 border ${filter === c.value ? "border-aawta-pink text-aawta-pink" : "border-white/15 text-white/60"}`}>
            {c.label.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-aawta-charcoal/60 text-overline text-white/50 text-left">
            <tr>
              <th className="px-4 py-3">Title</th><th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Owner</th><th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">v</th><th className="px-4 py-3">Updated</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-white/40">
                <FolderOpen className="mx-auto mb-3 text-white/30" size={32} />
                No documents in this view.
              </td></tr>
            )}
            {filtered.map((d) => {
              const isLink = d.resource_type === "link";
              return (
              <tr key={d.id} data-testid={`doc-row-${d.id}`} className="border-t border-white/5 hover:bg-white/5">
                <td className="px-4 py-3 text-white flex items-center gap-2">
                  {isLink ? <LinkIcon size={14} className="text-aawta-pink shrink-0" /> : <FileText size={14} className="text-aawta-pink shrink-0" />}
                  {isLink ? (
                    <a href={d.external_url} target="_blank" rel="noopener noreferrer" className="text-left hover:text-aawta-pink inline-flex items-center gap-1.5">
                      {d.title} <ExternalLink size={11} className="text-white/40" />
                    </a>
                  ) : (
                    <button onClick={() => setViewing(d)} className="text-left hover:text-aawta-pink">{d.title}</button>
                  )}
                </td>
                <td className="px-4 py-3 text-white/70 text-xs uppercase">{catLabel(d.category)}</td>
                <td className="px-4 py-3 text-white/70 text-xs">{d.assigned_owner_email || "—"}</td>
                <td className="px-4 py-3">
                  {editable ? (
                    <select value={d.status} onChange={(e) => updateMeta(d, { status: e.target.value })}
                      className={`bg-transparent border border-white/10 text-xs px-2 py-1 uppercase ${STATUS_COLOR[d.status]}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  ) : <span className={`text-xs uppercase ${STATUS_COLOR[d.status]}`}>{d.status}</span>}
                </td>
                <td className="px-4 py-3 text-white/60 font-mono text-xs">{isLink ? "—" : `v${d.current_version}`}</td>
                <td className="px-4 py-3 text-white/50 text-xs">{(d.updated_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3 text-right">
                  {isLink ? (
                    <a href={d.external_url} target="_blank" rel="noopener noreferrer" title="Open link" className="text-white/60 hover:text-aawta-pink mr-3 inline-flex"><ExternalLink size={14} /></a>
                  ) : (
                    <button onClick={() => download(d)} title="Download latest" className="text-white/60 hover:text-aawta-pink mr-3"><Download size={14} /></button>
                  )}
                  {editable && (
                    <>
                      {!isLink && (
                        <>
                          <button onClick={() => setUploadingVersion({ doc_id: d.id, notes: "", file: null, title: d.title })} title="Upload new version" className="text-white/60 hover:text-aawta-pink mr-3"><Upload size={14} /></button>
                          <button onClick={() => setViewing(d)} title="Versions" className="text-white/60 hover:text-aawta-pink mr-3"><History size={14} /></button>
                        </>
                      )}
                      <button onClick={() => remove(d)} title="Archive" className="text-white/60 hover:text-red-400"><Trash2 size={14} /></button>
                    </>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add link resource modal */}
      {creatingLink && (
        <Modal title="Add External Resource Link" onClose={() => setCreatingLink(null)}>
          <form onSubmit={async (e) => {
            e.preventDefault();
            if (!creatingLink.title || !creatingLink.external_url) { toast.error("Title + URL required"); return; }
            setSaving(true);
            try {
              await api.post("/admin/documents/link", creatingLink, { withCredentials: true });
              toast.success("Resource link added");
              setCreatingLink(null);
              await load();
            } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
            finally { setSaving(false); }
          }}>
            <div className="grid md:grid-cols-2 gap-4">
              <TextField label="Title *" value={creatingLink.title} onChange={(v) => setCreatingLink({ ...creatingLink, title: v })} testId="link-title" />
              <SelectField label="Category" value={creatingLink.category} onChange={(v) => setCreatingLink({ ...creatingLink, category: v })} options={CAT_VALUES} testId="link-category" />
              <div className="md:col-span-2">
                <TextField label="External URL *" value={creatingLink.external_url} onChange={(v) => setCreatingLink({ ...creatingLink, external_url: v })} placeholder="https://drive.google.com/…" testId="link-url" />
              </div>
              <label className="block">
                <span className="text-overline text-white/50 mb-1.5 block">Resource Type / Icon</span>
                <select data-testid="link-icon" value={creatingLink.icon} onChange={(e) => setCreatingLink({ ...creatingLink, icon: e.target.value })}
                  className="w-full bg-aawta-jet/60 border border-white/10 focus:border-aawta-pink outline-none text-white px-3 py-2 text-sm">
                  {LINK_ICONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
              <SelectField label="Status" value={creatingLink.status} onChange={(v) => setCreatingLink({ ...creatingLink, status: v })} options={STATUSES} testId="link-status" />
              <div className="md:col-span-2">
                <TextArea label="Description" value={creatingLink.description} onChange={(v) => setCreatingLink({ ...creatingLink, description: v })} testId="link-desc" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" onClick={() => setCreatingLink(null)} className="btn-ghost py-2.5">CANCEL</button>
              <button type="submit" data-testid="save-link-btn" disabled={saving} className="btn-pink disabled:opacity-50">{saving ? "SAVING…" : "ADD RESOURCE"}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Upload new document */}
      {creating && (
        <Modal title="Upload Document" onClose={() => setCreating(null)}>
          <form onSubmit={submitCreate} className="grid md:grid-cols-2 gap-4">
            <TextField label="Title *" value={creating.title} onChange={(v) => setCreating({ ...creating, title: v })} testId="doc-title" />
            <SelectField label="Category *" value={creating.category} onChange={(v) => setCreating({ ...creating, category: v })} options={CAT_VALUES} testId="doc-category" />
            <SelectField label="Status" value={creating.status} onChange={(v) => setCreating({ ...creating, status: v })} options={STATUSES} testId="doc-status" />
            <TextField label="Assigned Owner Email" value={creating.assigned_owner_email} onChange={(v) => setCreating({ ...creating, assigned_owner_email: v })} testId="doc-owner" />
            <div className="md:col-span-2">
              <TextArea label="Description" value={creating.description} onChange={(v) => setCreating({ ...creating, description: v })} testId="doc-desc" />
            </div>
            <div className="md:col-span-2">
              <TextField label="Version Notes" value={creating.notes} onChange={(v) => setCreating({ ...creating, notes: v })} testId="doc-notes" />
            </div>
            <div className="md:col-span-2">
              <span className="text-overline text-white/50 mb-1.5 block">File *</span>
              <input data-testid="doc-file" required type="file" onChange={(e) => setCreating({ ...creating, file: e.target.files?.[0] })}
                className="w-full bg-aawta-jet/60 border border-white/10 text-white px-3 py-2 text-sm file:bg-aawta-pink file:text-white file:border-0 file:px-3 file:py-1 file:mr-3" />
            </div>
            <div className="md:col-span-2 flex justify-end gap-3 mt-2">
              <button type="button" onClick={() => setCreating(null)} className="btn-ghost py-2.5">CANCEL</button>
              <button type="submit" data-testid="save-doc-btn" disabled={saving} className="btn-pink disabled:opacity-50">
                {saving ? "UPLOADING…" : "UPLOAD"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Upload a new version */}
      {uploadingVersion && (
        <Modal title={`New Version · ${uploadingVersion.title}`} onClose={() => setUploadingVersion(null)}>
          <form onSubmit={submitVersion} className="space-y-4">
            <div>
              <span className="text-overline text-white/50 mb-1.5 block">Replacement File *</span>
              <input data-testid="version-file" required type="file" onChange={(e) => setUploadingVersion({ ...uploadingVersion, file: e.target.files?.[0] })}
                className="w-full bg-aawta-jet/60 border border-white/10 text-white px-3 py-2 text-sm file:bg-aawta-pink file:text-white file:border-0 file:px-3 file:py-1 file:mr-3" />
            </div>
            <TextField label="Notes about this version" value={uploadingVersion.notes} onChange={(v) => setUploadingVersion({ ...uploadingVersion, notes: v })} testId="version-notes" />
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setUploadingVersion(null)} className="btn-ghost py-2.5">CANCEL</button>
              <button type="submit" data-testid="save-version-btn" disabled={saving} className="btn-pink disabled:opacity-50">
                {saving ? "UPLOADING…" : "ADD VERSION"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Version history viewer */}
      {viewing && (
        <Modal title={viewing.title} onClose={() => setViewing(null)}>
          <div className="space-y-4">
            {viewing.description && <p className="text-sm text-white/70">{viewing.description}</p>}
            <div className="flex gap-4 text-xs text-white/50">
              <span>Category: <span className="text-white">{catLabel(viewing.category)}</span></span>
              <span>Status: <span className={STATUS_COLOR[viewing.status]}>{viewing.status.toUpperCase()}</span></span>
              <span>Owner: <span className="text-white">{viewing.assigned_owner_email || "—"}</span></span>
            </div>
            <p className="text-overline text-aawta-pink pt-3">VERSION HISTORY ({viewing.versions.length})</p>
            <div className="space-y-2">
              {[...viewing.versions].reverse().map((v) => (
                <div key={v.version} data-testid={`version-${v.version}`} className="border border-white/10 p-4 flex items-start gap-4">
                  <div className="font-display text-2xl text-aawta-pink w-12 shrink-0">v{v.version}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{v.original_filename}</p>
                    <p className="text-xs text-white/50 mt-1">
                      {v.uploaded_by} · {v.uploaded_at.slice(0, 10)} · {(v.size / 1024).toFixed(1)} KB
                    </p>
                    {v.notes && <p className="text-xs text-white/70 mt-2 italic">&ldquo;{v.notes}&rdquo;</p>}
                  </div>
                  <button onClick={() => download(viewing, v.version)} className="text-white/60 hover:text-aawta-pink" title="Download this version">
                    <Download size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
